import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LoggingService } from '@newroom-connect/library/services';
import { DoubleClickDirective, ClickOutsideDirective } from '@newroom-connect/library/directives';
import { DomHelper } from '@newroom-connect/library/helpers';

export interface IDragPointerEvent {
  x: number;
  y: number;
}

/**
 * Simple Angular component for hotspot drag overlay.
 */
@Component({
  selector: 'nrc-hotspot-drag-overlay',
  standalone: true,
  imports: [CommonModule, DoubleClickDirective, ClickOutsideDirective],
  templateUrl: './hotspot-drag-overlay.component.html'
})
export class HotspotDragOverlayComponent implements OnInit {
  private readonly logger = inject(LoggingService);

  @ViewChild('dragSurface', { static: true }) private dragSurfaceRef!: ElementRef<HTMLElement>;

  @Input() public draggable = true;
  @Input() public hotspotId!: string;
  @Input() public viewerElement!: HTMLElement;
  @Input() public startOffsetX = 0;
  @Input() public startOffsetY = 0;

  @Output() public dragMoveEvent = new EventEmitter<IDragPointerEvent>();
  @Output() public dragStartEvent = new EventEmitter<void>();
  @Output() public dragEndEvent = new EventEmitter<void>();
  @Output() public clickEvent = new EventEmitter<MouseEvent>();
  @Output() public doubleClickEvent = new EventEmitter<MouseEvent>();
  @Output() public clickOutsideEvent = new EventEmitter<MouseEvent>();

  public surfaceStyles: unknown = {};

  private dragElement!: HTMLElement;
  private isDragging = false;
  private pointerActive = false;

  /**
   *
   */
  public ngOnInit(): void {
    this.dragElement = this.dragSurfaceRef.nativeElement;

    if (!this.dragElement || !this.viewerElement) {
      return;
    }

    DomHelper.setPointerEvents(this.dragElement, 'auto');
  }

  /**
   * Update draggable state and cursor.
   * @param enabled
   */
  public setDraggable(enabled: boolean): void {
    this.draggable = enabled;
    this.updateCursor();
  }

  /**
   * Handle single click events from the double-click directive.
   * @param event
   */
  public onClick(event: MouseEvent): void {
    this.clickEvent.emit(event);
  }

  /**
   * Handle double click events from the double-click directive.
   * @param event
   */
  public onDoubleClick(event: MouseEvent): void {
    this.doubleClickEvent.emit(event);
  }

  /**
   * Handle click outside events from the click-outside directive.
   * @param event
   */
  public onClickOutside(event: MouseEvent): void {
    this.clickOutsideEvent.emit(event);
  }

  /**
   * Handle pointer down events for drag initiation.
   * @param event
   */
  public onPointerDown(event: PointerEvent): void {
    this.pointerActive = true;
    this.isDragging = false;

    this.updateCursor();
    DomHelper.safeSetPointerCapture(this.dragElement, event.pointerId);
  }

  /**
   * Handle pointer move events for dragging.
   * @param event
   */
  public onPointerMove(event: PointerEvent): void {
    if (!this.pointerActive || !this.draggable) {
      return;
    }

    // Start dragging after threshold
    if (!this.isDragging) {
      this.beginDrag();
    }

    // Get relative pointer position and apply start offset
    const relativePointer = DomHelper.getRelativePointer(event, this.viewerElement);
    const targetX = relativePointer.x - this.startOffsetX;
    const targetY = relativePointer.y - this.startOffsetY;

    // Emit raw pointer coordinates
    this.dragMoveEvent.emit({ x: targetX, y: targetY });
  }

  /**
   * Handle pointer up events to end dragging.
   * @param event
   */
  public onPointerUp(event: PointerEvent): void {
    DomHelper.safeReleasePointerCapture(this.dragElement, event.pointerId);
    this.endPointerInteraction();
  }

  /**
   * Handle pointer end events (cancel/leave).
   */
  public onPointerEnd(): void {
    this.endPointerInteraction();
  }

  /**
   * Update the cursor based on the dragging state and draggable state.
   */
  private updateCursor(): void {
    const cursor = this.isDragging ? 'grabbing' : (this.draggable ? 'grab' : 'pointer');

    DomHelper.setCursor(this.dragElement, cursor);
  }

  /**
   * Begin dragging and update the cursor.
   */
  private beginDrag(): void {
    this.isDragging = true;
    this.updateCursor();

    // Emit drag start event - let parent handle camera controls
    this.dragStartEvent.emit();
  }

  /**
   * End pointer interaction and update the cursor.
   */
  private endPointerInteraction(): void {
    const wasDragging = this.isDragging;

    this.isDragging = false;
    this.pointerActive = false;
    this.updateCursor();

    // Emit drag end event if we were dragging - let parent handle camera controls
    if (wasDragging) {
      this.dragEndEvent.emit();
    }
  }
}
